import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { fetchPipelineAudit } from '@/lib/adminApi';

const KINDS = [
  {
    id: '',
    label: 'All',
    detail: 'One row per incident. Later polls update that row instead of adding another.',
  },
  {
    id: 'celery_failure',
    label: 'Celery failure',
    detail: 'The current Celery task is FAILURE, or a failed job’s error came from Celery. Written as soon as it is seen.',
  },
  {
    id: 'other_failure',
    label: 'Other failure',
    detail: 'A child job failed, Celery finished while the job was still running, or the resume could not be queued.',
  },
  {
    id: 'hung_idle',
    label: 'Hung idle',
    detail: 'The job stopped updating and no Celery task is active, or the task is still STARTED / RETRY. Written after 3 minutes without an update.',
  },
];

const ACTION_LABEL = {
  resume: 'Resumed the pipeline',
  fail: 'Marked the job failed',
  failed: 'Logged the failure',
  report: 'Reported only',
  enqueue_failed: 'Could not queue the resume',
};

function kindBadge(kind) {
  if (kind === 'celery_failure') return <Badge className="bg-red-600">Celery failure</Badge>;
  if (kind === 'other_failure') return <Badge className="bg-amber-600">Other failure</Badge>;
  if (kind === 'hung_idle') return <Badge className="bg-slate-600">Hung idle</Badge>;
  return <Badge variant="outline">{kind || '—'}</Badge>;
}

function actionLabel(row) {
  const base = ACTION_LABEL[row.action] || row.action || '—';
  const attempt = Number(row.attempt);
  if (!Number.isFinite(attempt) || attempt <= 0) return base;
  return `${base} · attempt ${attempt}`;
}

function cell(value) {
  const text = value == null || value === '' ? '—' : String(value);
  return text;
}

export function AdminPipelineAuditPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPipelineAudit({ page, pageSize, kind, q: search });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load the pipeline audit');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, kind, search]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const selected = KINDS.find((item) => item.id === kind) || KINDS[0];
  const countFor = (id) => {
    if (!id) return (counts.celery_failure || 0) + (counts.other_failure || 0) + (counts.hung_idle || 0);
    return counts[id] || 0;
  };

  return (
    <section className="glass-panel min-w-0 rounded-2xl p-4" data-testid="admin-pipeline-audit">
      <div className="mb-3">
        <h2 className="font-heading text-lg font-semibold">Pipeline audit</h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          {selected.detail} After two resumes the job is marked failed, and that reason stays on the same row.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {KINDS.map((item) => (
          <Button
            key={item.id || 'all'}
            type="button"
            size="sm"
            variant={kind === item.id ? 'default' : 'outline'}
            className="rounded-full"
            onClick={() => {
              setKind(item.id);
              setPage(1);
            }}
            data-testid={`admin-audit-kind-${item.id || 'all'}`}
          >
            {item.label}
            <span className="ml-1 text-xs opacity-70">{countFor(item.id)}</span>
          </Button>
        ))}
        <form
          className="ml-auto flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
        >
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              className="h-9 w-56 pl-8"
              placeholder="Job, project, or reason"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              data-testid="admin-audit-search"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">
            Search
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => load()}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
        </form>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200/80">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Celery task</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Last step</TableHead>
                <TableHead>Auditor</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="py-12 text-center text-slate-500">
                    No audit rows yet
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id || `${row.job_id}-${row.kind}-${row.attempt}`}>
                    <TableCell className="max-w-[180px] align-top text-xs">
                      <div className="font-mono">{cell(row.job_id)}</div>
                      <div className="text-slate-400">{cell(row.job_type)}</div>
                    </TableCell>
                    <TableCell className="max-w-[160px] align-top font-mono text-xs">{cell(row.project_id)}</TableCell>
                    <TableCell className="align-top">{kindBadge(row.kind)}</TableCell>
                    <TableCell className="max-w-sm align-top whitespace-normal text-xs">{cell(row.reason)}</TableCell>
                    <TableCell className="max-w-[180px] align-top font-mono text-xs">{cell(row.celery_task_id)}</TableCell>
                    <TableCell className="align-top text-xs">{cell(row.celery_state)}</TableCell>
                    <TableCell className="align-top text-xs">{row.progress == null ? '—' : `${row.progress}%`}</TableCell>
                    <TableCell className="max-w-[200px] align-top whitespace-normal text-xs">{cell(row.message)}</TableCell>
                    <TableCell className="max-w-[180px] align-top whitespace-normal text-xs">{actionLabel(row)}</TableCell>
                    <TableCell className="align-top whitespace-nowrap text-xs">{cell(row.updated_at || row.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
        <span>
          {total} row{total === 1 ? '' : 's'}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>
            Page {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
